import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Parallax } from 'react-scroll-parallax';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import ContactFormModal from '../../components/ContactForm/ContactFormModal';
import { AnimatedBackground } from '../../components/AnimatedBackground';
import { useAOS, useMotionVariants, viewportSettings } from '../../hooks/useAnimations';
import { useShouldReduceAnimations, useIsMobile } from '../../hooks/useDeviceDetection';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import { 
  ArrowRight,
  Mail,
  Phone,
  ChevronDown,
  Trophy,
  Rocket,
  Code,
  Globe,
  Clock,
  Database,
  Cog,
  Menu,
  X
} from 'lucide-react';

// Lazy load video component for better initial load
const LandingVideoDemo = lazy(() => import('../../components/Landing/LandingVideoDemo'));

// Register GSAP plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const LandingPage: React.FC = () => {
  const { i18n, ready } = useTranslation();
  const navigate = useNavigate();
  const [forceReady, setForceReady] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const shouldReduceAnimations = useShouldReduceAnimations();
  const isMobile = useIsMobile();

  // Get environment variable for showing login button
  const showLoginButton = import.meta.env.VITE_SHOW_LOGIN_BUTTON === 'true';

  // Initialize AOS animations (optimized for mobile)
  useAOS({
    duration: shouldReduceAnimations ? 0 : 800,
    once: true,
    offset: 50,
  });

  // Scroll progress for parallax effects - DISABLED opacity/scale to prevent hero disappearing
  // const { scrollYProgress } = useScroll();
  // const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  // const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95]);

  // Force ready state after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // GSAP ScrollTrigger for hero section - DISABLED on mobile and to prevent hero disappearing
  useEffect(() => {
    if (!heroRef.current || shouldReduceAnimations) return;

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [forceReady, shouldReduceAnimations]);

  // Use enhanced animation variants from hook (optimized for mobile) - MUST be before conditional return
  const motionVariants = useMotionVariants();
  const { fadeInUp, fadeIn, staggerContainer, scaleIn } = motionVariants;

  // NUEVO: Servicios profesionales redefinidos - Defined before conditional return to maintain hook order
  const professionalServices = React.useMemo(() => [
    {
      icon: <Code className="w-8 h-8" />,
      title: i18n.language === 'en' ? "Development of Sports Platforms" : "Desarrollo de Plataformas Deportivas",
      description: i18n.language === 'en' ? "Custom platforms for player management, data analysis and process automation specifically designed for sports clubs" : "Plataformas personalizadas para gestión de jugadores, análisis de datos y automatización de procesos específicamente diseñadas para clubes deportivos"
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: i18n.language === 'en' ? "Big Data applied to Sport" : "Big Data aplicado al Deporte", 
      description: i18n.language === 'en' ? "Performance analysis systems, interactive dashboards and automated reports for data-driven decision making" : "Sistemas de análisis de rendimiento, dashboards interactivos y reportes automatizados para toma de decisiones basada en datos"
    },
    {
      icon: <Cog className="w-8 h-8" />,
      title: i18n.language === 'en' ? "Tech Project Management" : "Gestión de Proyectos Tech",
      description: i18n.language === 'en' ? "End-to-end leadership in technology solution implementation, from requirements analysis to go-live" : "Liderazgo integral en implementación de soluciones tecnológicas, desde análisis de requisitos hasta puesta en marcha"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: i18n.language === 'en' ? "System Integration" : "Integración de Sistemas",
      description: i18n.language === 'en' ? "Connecting different third-party or club data sources into unified systems for greater operational efficiency" : "Conexión de diferentes fuentes de datos de terceros o del club en sistemas unificados para mayor eficiencia operativa"
    }
  ], [i18n.language]);

  // Force re-render when translations are ready
  if (!ready || !forceReady) {
    return <LoadingSpinner fullscreen />;
  }

  const scrollToSection = (sectionId: string): void => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 64; // Height of fixed navbar (h-14 sm:h-16)
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
   <>
      {/* Navigation - Improved Minimalist Design with Mobile Hamburger */}
      <motion.nav 
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="fixed top-0 w-full z-[9999] bg-white/95 backdrop-blur-md border-b border-blue-100 shadow-sm"
      >
          <div className="max-w-7l mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              {/* Logo - Left side on mobile, centered on desktop */}
              <div className="flex items-center">
                <img
                  src="/images/logo_scoutmind.png"
                  alt="ScoutMind AI Logo"
                  className="h-8 w-auto mr-2"
                  style={{ objectFit: 'contain', display: 'block' }}
                />
              </div>


              {/* Desktop Navigation - Hidden on mobile */}
              <div className="hidden md:flex items-center justify-center flex-1 space-x-1 lg:space-x-2">
                <motion.button 
                  onClick={() => scrollToSection('services')} 
                  className="px-3 py-1.5 lg:px-4 lg:py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-all duration-200 font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {i18n.language === 'en' ? 'Services' : 'Servicios'}
                </motion.button>
                <motion.button 
                  onClick={() => scrollToSection('about')} 
                  className="px-3 py-1.5 lg:px-4 lg:py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-all duration-200 font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {i18n.language === 'en' ? 'About' : 'Acerca de'}
                </motion.button>
                <motion.button 
                  onClick={() => scrollToSection('contact')} 
                  className="px-3 py-1.5 lg:px-4 lg:py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-all duration-200 font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {i18n.language === 'en' ? 'Contact' : 'Contacto'}
                </motion.button>
              </div>
              <div className="flex items-center space-x-3 ml-auto">
                <div className="hidden md:flex items-center ml-auto">
                  <LanguageSelector 
                    variant="compact" 
                    className="text-blue-700" 
                  />
                </div>
                {showLoginButton && (
                  <motion.button
                    onClick={() => navigate('/login')}
                    className="hidden md:block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {i18n.language === 'en' ? 'Login' : 'Iniciar sesión'}
                  </motion.button>
                )}
              

               {/* Mobile Menu Button - Right side */}
               <div className="flex items-center md:hidden">
                <motion.button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                  whileTap={{ scale: 0.95 }}
                  aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </motion.button>
               </div>
              </div>
            </div>

            {/* Mobile Menu - Dropdown */}
            <AnimatePresence>
              {isMobileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="md:hidden border-t border-blue-100 overflow-visible"
                  style={{ position: 'relative', zIndex: 9998 }}
                >
                  <div className="py-3 space-y-1">
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setTimeout(() => scrollToSection('services'), 250);
                      }} 
                      className="w-full text-left px-4 py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                    >
                      {i18n.language === 'en' ? 'Services' : 'Servicios'}
                    </button>
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setTimeout(() => scrollToSection('about'), 250);
                      }} 
                      className="w-full text-left px-4 py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                    >
                      {i18n.language === 'en' ? 'About' : 'Acerca de'}
                    </button>
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setTimeout(() => scrollToSection('contact'), 250);
                      }} 
                      className="w-full text-left px-4 py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors font-medium shadow-sm"
                    >
                      {i18n.language === 'en' ? 'Contact' : 'Contacto'}
                    </button>
                    {showLoginButton && (
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          navigate('/login');
                        }}
                        className="w-full text-left px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-md"
                      >
                        {i18n.language === 'en' ? 'Login' : 'Iniciar sesión'}
                      </button>
                    )}
                    <div className="px-4 py-2 relative" style={{ zIndex: 99999 }}>
                      <LanguageSelector 
                        variant="compact" 
                        className="text-blue-700 w-full" 
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.nav>

      {/* Hero Section with Enhanced Animations */}
      <AnimatedBackground variant="hero" className="w-full min-h-screen">
        {/* Hero Section */}
        <div className="pt-14 sm:pt-16">
          <motion.section 
            ref={heroRef}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
            className="relative min-h-screen flex items-center justify-center overflow-hidden"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              preload={isMobile ? "none" : "auto"}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: -2,
              }}
            >
              <source src="/video/Intro-ScoutMindai.mp4" type="video/mp4; codecs=avc1.42E01E,mp4a.40.2" />
              <source src="/video/Intro-ScoutMindai.mp4" type="video/mp4" />
              Tu navegador no soporta la reproducción del video.
            </video>

            <div
              style={{
                position: 'absolute',
                top: 0,
                left:0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(18, 24, 46, 0.7)', // azul con transparencia para ver video detrás
                zIndex: -1, // overlay azul encima del video pero debajo del contenido
              }}
            />

            {/* Disable parallax on mobile for better performance */}
            {shouldReduceAnimations ? (
              <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
                <motion.h1 
                    variants={fadeInUp} 
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight"
                  >
                    {i18n.language === 'en' ? 'Consultancy in' : 'Consultoría en'}

                    <motion.span 
                      className="block bg-gradient-to-r from-blue-300 via-blue-100 to-purple-200 bg-clip-text text-transparent"
                      initial={{ backgroundPosition: '0% 50%' }}
                      animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                      transition={{ duration: 5, repeat: Infinity }}
                      style={{ backgroundSize: '200% 200%', color: '#96AFFF' }}
                    >
                      {i18n.language === 'en' ? 'Sport Tech' : 'Sport Tech'}
                    </motion.span>

                    <motion.span
                      className="block font-bold text-white" // mismo estilo que 'consultoría en'
                    >
                      {i18n.language === 'en' ? ' for sports clubs' : ' para clubes deportivos'}
                    </motion.span>
                  </motion.h1>

                
                <motion.p variants={fadeInUp} className="text-base sm:text-lg md:text-xl text-blue-100 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
                  {i18n.language === 'en' ? 
                    'Optimize your processes, reduce costs, and embrace true digital transformation.' : 
                    'Optimiza tus procesos, ahorra costes y únete a la verdadera digitalización.'
                  }
                </motion.p>
                
                <motion.div variants={scaleIn} className="mb-12">
                  <motion.button
                    onClick={() => setIsContactModalOpen(true)}
                    className="group relative px-8 py-4 bg-white hover:bg-blue-50 text-gray-800 font-bold text-lg rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl overflow-hidden"
                    whileHover={{ scale: 1.05, y: -4 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      
                      {i18n.language === 'en' ? 'Contact now' : 'Contacta ahora'}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-10"
                      initial={false}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.button>
                </motion.div>
              </div>
            ) : (
              <Parallax speed={-10}>
                <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
                  <motion.h1 
                    variants={fadeInUp} 
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight"
                  >
                    {i18n.language === 'en' ? 'Consultancy in' : 'Consultoría en'}

                    <motion.span 
                      className="block bg-gradient-to-r from-blue-300 via-blue-100 to-purple-200 bg-clip-text text-transparent"
                      initial={{ backgroundPosition: '0% 50%' }}
                      animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                      transition={{ duration: 5, repeat: Infinity }}
                      style={{ backgroundSize: '200% 200%', color: '#96AFFF' }}
                    >
                      {i18n.language === 'en' ? 'Sport Tech' : 'Sport Tech'}
                    </motion.span>

                    <motion.span
                      className="block font-bold text-white"
                    >
                      {i18n.language === 'en' ? ' for sports clubs' : ' para clubes deportivos'}
                    </motion.span>
                  </motion.h1>

                  <motion.p variants={fadeInUp} className="text-base sm:text-lg md:text-xl text-blue-100 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
                    {i18n.language === 'en' ? 
                      'Optimize your processes, reduce costs, and embrace true digital transformation.' : 
                      'Optimiza tus procesos, ahorra costes y únete a la verdadera digitalización.'
                    }
                  </motion.p>

                  <motion.div variants={scaleIn} className="mb-12">
                    <motion.button
                      onClick={() => setIsContactModalOpen(true)}
                      className="group relative px-8 py-4 bg-white hover:bg-blue-50 text-gray-800 font-bold text-lg rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl overflow-hidden"
                      whileHover={{ scale: 1.05, y: -4 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        {i18n.language === 'en' ? 'Contact now' : 'Contacta ahora'}
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-10"
                        initial={false}
                        transition={{ duration: 0.3 }}
                      />
                    </motion.button>
                  </motion.div>
                </div>
              </Parallax>
            )}

            <motion.div 
              variants={fadeIn}
              className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ChevronDown className="w-8 h-8 text-white/60" />
              </motion.div>
            </motion.div>
          </motion.section>
        </div>
      </AnimatedBackground>

      {/* Services Section with AOS animations - optimized for mobile */}
      <div className="bg-white">
        <motion.section 
          id="services" 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05, margin: shouldReduceAnimations ? '50px' : '200px' }}
          variants={staggerContainer}
          className="py-20 bg-white relative overflow-hidden"
        >
          {/* Background decoration with parallax - disabled on mobile */}
          {!shouldReduceAnimations && (
            <Parallax speed={-10} className="absolute top-0 right-0 w-96 h-96 opacity-5">
              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 rounded-full blur-3xl" />
            </Parallax>
          )}

          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10">
            <motion.div 
              variants={fadeInUp} 
              className="text-left mb-16"
              data-aos="fade-up"
            >
              <h2 className="text-sm sm:text-sm md:text-sm uppercase font-bold bg-gradient-to-b from-blue-900 to-blue-500 bg-clip-text text-transparent mb-4">
                {i18n.language === 'en' ? 'Services' : 'Servicios'}
              </h2>
              <p className="text-4xl font-bold text-gray-900 max-w-5xl">
                {i18n.language === 'en' ? 'Discover what Big Data and Artificial Intelligence can do for your organisation.' : 'Descubre lo que el Big Data y la Inteligencia Artificial puede hacer por tu organización.'}
              </p>
            </motion.div>

            <motion.div 
              variants={staggerContainer} 
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.05, margin: '100px' }}
            >
              {professionalServices.map((service, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  data-aos="fade-up"
                  data-aos-delay={shouldReduceAnimations ? 0 : index * 50}
                  className="group relative"
                  whileHover={shouldReduceAnimations ? {} : { y: -8, scale: 1.02 }}
                >
                  {/* Hover gradient overlay */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={false}
                  />
                  
                  <div className="relative z-10">
                    {/* Icono cuadrado suave - with min-height to prevent layout shift */}
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-100 mb-6 min-h-[48px]">
                      <div className="text-blue-700">
                        {service.icon}
                      </div>
                    </div>

                    {/* Título con tipografía clara - with min-height to prevent layout shift */}
                    <h3 className="text-lg font-bold text-blue-900 mb-2 min-h-[28px]">
                      {service.title}
                    </h3>

                    <p className="text-sm text-black-800 font-semibold leading-relaxed group-hover:text-blue-900 transition-colors duration-300 min-h-[80px]">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Video Demo Section - Lazy loaded for better performance */}
            <motion.div
              variants={fadeInUp}
              className="mt-20 max-w-5xl mx-auto"
              data-aos="fade-up"
            >
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={viewportSettings}
                  transition={{ duration: isMobile ? 0.2 : 0.5 }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full border-2 border-blue-300 mb-4"
                >
                  <span className="text-blue-900 font-semibold text-sm">
                    {i18n.language === 'en' ? 'Tailored Solutions' : 'Soluciones Adaptadas'}
                  </span>
                </motion.div>
              </div>

              <div className="">
                <Suspense fallback={
                  <div className="flex items-center justify-center min-h-[400px]">
                    <LoadingSpinner />
                  </div>
                }>
                  <LandingVideoDemo />
                </Suspense>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Clients Section */}
        <motion.section
          id="clients"
          initial="hidden"
          whileInView="visible"
          viewport={viewportSettings}
          variants={staggerContainer}
          className="py-20 bg-gray-50"
        >
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-12">
            
            {/* Textos a la izquierda */}
            <motion.div
              variants={fadeInUp}
              className="flex-1 text-left"
            >
              <h2 className="text-sm uppercase font-bold text-blue-900 tracking-wide mb-2">
                {i18n.language === 'en' ? 'Start the game' : 'Empieza el juego'}
              </h2>
              <p className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                {i18n.language === 'en'
                  ? 'Current collaborations with clubs and entities'
                  : 'Colaboraciones actuales con clubes y entidades'}
              </p>
            </motion.div>

            {/* Logos a la derecha */}
            <motion.div
              variants={fadeInUp}
              className="flex-1 flex items-center justify-center md:justify-center gap-10 flex-wrap"
            >
              {/* RC Deportivo */}
              <a
                href="https://www.laliga.com/laliga-hypermotion"
                target="_blank"
                rel="noopener noreferrer"
                className="text-center transition-transform duration-300 hover:scale-105"
              >
                <img
                  src="/images/logo_ligahiper.png"
                  alt="Liga"
                  loading="lazy"
                  className="w-40 h-40 object-contain mx-auto mb-2 rounded-md"
                />
                 <p className="text-gray-700 font-bold text-sm">
                  {i18n.t('clients.ligaHypermotion_line1')}<br />{i18n.t('clients.ligaHypermotion_line2')}
                </p>
              </a>

              {/* Soccer Central */}
              <a
                href="https://soccercentralsa.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-center transition-transform duration-300 hover:scale-105"
              >
                <img
                  src="/images/logo_soccer_central.jpeg"
                  alt="Soccer Central"
                  loading="lazy"
                  className="w-40 h-40 object-contain mx-auto mb-2 rounded-md"
                />
                <p className="text-gray-700 font-bold text-sm">
                  Soccer Central<br />San Antonio
                </p>
              </a>
            </motion.div>
          </div>
        </motion.section>


        {/* About Section - Restructured with Image Background and Footer */}

        <motion.section
          id="about"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="relative overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #000000 0%, #0F2A7D 50%, #12182E 100%)' }}
        >
          {/* Contenedor de la imagen cuadrada */}
          <div className="max-w-9xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 mt-6">
            
            {/* Imagen cuadrada centrada */}
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl z-10">
              <img
                src="/images/fondo.png"
                alt="Fondo principal"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-85"
                style={{
                  filter: 'brightness(1.35) contrast(1.3) saturate(1.15)', // más luz y color
                  transform: 'scale(1.02)',
                  transition: 'all 0.6s ease-in-out',
                }}
      />

              {/* Overlay oscuro para contraste */}
              <div className="absolute inset-0 bg-black/30" />

              {/* Contenido centrado dentro del cuadrado */}
              <div className="relative z-20 flex flex-col items-center justify-center text-center h-full px-6">
                <motion.div variants={fadeInUp}>
                  <p className="text-sm sm:text-base text-white/90 mb-4 uppercase tracking-wider font-semibold">
                    {i18n.t('about.brandName')}
                  </p>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
                    {i18n.t('about.title')}
                  </h2>
                  <motion.button
                    onClick={() => setIsContactModalOpen(true)}
                    className="inline-block px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {i18n.t('about.ctaButton')}
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Footer de tres columnas */}
          <div className="relative z-30 w-full py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <motion.div
                variants={staggerContainer}
                className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12"
              >
                {/* Columna 1 */}
                <motion.div variants={fadeInUp} className="text-center md:text-left">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    {i18n.t('about.benefits.saveTime.title')}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-200 leading-relaxed">
                    {i18n.t('about.benefits.saveTime.description')}
                  </p>
                </motion.div>

                {/* Columna 2 */}
                <motion.div variants={fadeInUp} className="text-center md:text-left">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    {i18n.t('about.benefits.betterDecisions.title')}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-200 leading-relaxed">
                    {i18n.t('about.benefits.betterDecisions.description')}
                  </p>
                </motion.div>

                {/* Columna 3 */}
                <motion.div variants={fadeInUp} className="text-center md:text-left">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    {i18n.t('about.benefits.tailored.title')}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-200 leading-relaxed">
                    {i18n.t('about.benefits.tailored.description')}
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.section>


        {/* Get in Touch - Behind ScoutMind AI Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={viewportSettings}
          variants={staggerContainer}
          className="py-20 bg-gray-50"
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            {/* Section Header */}
            <motion.div variants={fadeInUp} className="text-left mb-12">
              <h2 className="text-sm uppercase font-bold text-blue-900 tracking-wide mb-2">
                {i18n.t('getInTouch.sectionTitle')}
              </h2>
              <h3 className="text-l sm:text-l md:text-l font-bold text-gray-900">
                {i18n.t('getInTouch.name')}
              </h3>
            </motion.div>

            {/* First Row: Photo + About Me */}
            <motion.div
              variants={fadeInUp}
              className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-16"
            >
              {/* Personal Photo - Background version */}
              <motion.div
                variants={scaleIn}
                className=""
              >
                <div
                  className="w-full h-full aspect-square rounded-2xl overflow-hidden shadow-lg relative bg-gradient-to-br from-gray-200 to-gray-300"
                  style={{
                    backgroundImage: "url('/images/me.png')",
                    backgroundSize: '100%',        // controla el “zoom” (90–110%)
                    backgroundPosition: '5% 0%', // controla el encuadre (x% y%)
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: 'transparent',   // fallback gris si la imagen tarda en cargar
                    }}
                />
              </motion.div>

              {/* About Me Content */}
              <motion.div
                variants={fadeInUp}
                className="flex flex-col justify-center"
              >
                <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                  {i18n.t('getInTouch.aboutMeTitle')}
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  {i18n.t('getInTouch.aboutMeText')}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  {i18n.t('getInTouch.aboutMeText2')}
                </p>
                <div>
                  <a
                    href="https://www.linkedin.com/in/pedro-sc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-4 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    style={{ background: '#0F2A7D' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#0A1F5C'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
                  >
                    {i18n.t('getInTouch.linkedinButton')}
                  </a>
                </div>
              </motion.div>
            </motion.div>

            {/* Second Row: Career Text + Projects Image */}
            <motion.div
              variants={fadeInUp}
              className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center"
            >
              {/* Career Trajectory Text */}
              <motion.div variants={fadeInUp} className="order-2 md:order-1">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  {i18n.t('getInTouch.careerTitle')}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  {i18n.t('getInTouch.careerText')}
                </p>
                <div>
                  <a
                    href="https://www.instagram.com/hoopsdatabasketball/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-4 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    style={{ background: '#0F2A7D' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#0A1F5C'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
                  >
                    {i18n.t('getInTouch.instagramButton')}
                  </a>
                </div>
              </motion.div>

              {/* Projects Image */}
              <motion.div
                variants={scaleIn}
                className="order-1 md:order-2 flex items-center justify-center"
              >
                <div className="w-full rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="/images/projects-1.png"
                    alt="Hoops Data Basketball Projects"
                    loading="lazy"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* How We Work Section */}
        <motion.section
          id="how-we-work"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="py-20 relative bg-white"
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <motion.div variants={fadeInUp} className="text-left mb-16">
              <h2 className="text-xs sm:text-sm uppercase font-bold text-blue-900 tracking-wide mb-4">
                {i18n.t('landing.howWeWork.sectionTitle')}
              </h2>
              <p className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                {i18n.t('landing.howWeWork.subtitle')}
              </p>
            </motion.div>

            {/* Timeline de proceso */}
            <motion.div variants={fadeIn} className="max-w-6xl mx-auto mb-20">
              <div className="relative">
                {/* Línea horizontal en desktop - más sutil */}
                <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gray-300 mx-16"></div>

                <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
                  {/* Step 1 */}
                  <motion.div variants={scaleIn} className="relative">
                    <div className="flex flex-col items-center md:items-center text-center">
                      <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mb-6 relative z-10">
                        <span className="text-blue-900 text-2xl font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">
                          {i18n.t('landing.howWeWork.steps.step1.title')}
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {i18n.t('landing.howWeWork.steps.step1.description')}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Step 2 */}
                  <motion.div variants={scaleIn} className="relative">
                    <div className="flex flex-col items-center md:items-center text-center">
                      <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mb-6 relative z-10">
                        <span className="text-blue-900 text-2xl font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">
                          {i18n.t('landing.howWeWork.steps.step2.title')}
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {i18n.t('landing.howWeWork.steps.step2.description')}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Step 3 */}
                  <motion.div variants={scaleIn} className="relative">
                    <div className="flex flex-col items-center md:items-center text-center">
                      <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mb-6 relative z-10">
                        <span className="text-blue-900 text-2xl font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">
                          {i18n.t('landing.howWeWork.steps.step3.title')}
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {i18n.t('landing.howWeWork.steps.step3.description')}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Step 4 */}
                  <motion.div variants={scaleIn} className="relative">
                    <div className="flex flex-col items-center md:items-center text-center">
                      <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mb-6 relative z-10">
                        <span className="text-blue-900 text-2xl font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">
                          {i18n.t('landing.howWeWork.steps.step4.title')}
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {i18n.t('landing.howWeWork.steps.step4.description')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>

            {/* Modelos de colaboración */}
            <motion.div variants={fadeInUp} className="text-left mb-16">
              <h2 className="text-sm uppercase font-bold text-blue-900 tracking-wide mb-2">
                {i18n.language === 'en' ? 'Flexible Collaboration Models' : 'Modelos de Colaboración Flexibles'}
              </h2>
              <p className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                {i18n.language === 'en' ? 'Choose the option that best fits your club' : 'Elige la opción que más se adapte a tu club'}
              </p>
            </motion.div>

            <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Modelo 1: Proyecto Completo */}
              <motion.div variants={scaleIn} className="bg-white rounded-2xl p-8 text-center border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 group hover:shadow-2xl hover:shadow-blue-500/20 relative overflow-hidden aos-init aos-animate">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: '#5B9BD5' }}>
                    <Rocket className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {i18n.language === 'en' ? 'Complete Project' : 'Proyecto completo'}
                  </h4>
                  <p className="text-gray-500 text-sm">
                    {i18n.language === 'en' ? 'Fixed Scope & Budget' : 'Alcance y presupuesto fijo'}
                  </p>
                </div>

                <ul className="space-y-3 mb-6 text-left">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Defined scope and deliverables' : 'Alcance y entregables definidos'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Fixed timeline and milestones' : 'Plazos e hitos fijos'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Transparent fixed budget' : 'Presupuesto fijo transparente'}</span>
                  </li>
                </ul>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-600 text-xs text-center">
                    {i18n.language === 'en' ? 'Perfect for: Platforms, dashboards, specific systems' : 'Perfecto para: Plataformas, dashboards, sistemas específicos'}
                  </p>
                </div>
              </motion.div>

              {/* Modelo 2: Consultoría por Horas */}
              <motion.div variants={scaleIn} className="bg-white rounded-2xl p-8 text-center border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 group hover:shadow-2xl hover:shadow-blue-500/20 relative overflow-hidden aos-init aos-animate">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: '#1E3A8A' }}>
                    <Clock className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {i18n.language === 'en' ? 'Hourly Consulting' : 'Consultoría por Horas'}
                  </h4>
                  <p className="text-gray-500 text-sm">
                    {i18n.language === 'en' ? 'Maximum Flexibility' : 'Máxima Flexibilidad'}
                  </p>
                </div>

                <ul className="space-y-3 mb-6 text-left">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Pay only for time used' : 'Paga solo por tiempo utilizado'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Flexible scope adjustment' : 'Ajuste flexible del alcance'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Weekly transparent reporting' : 'Reporte transparente semanal'}</span>
                  </li>
                </ul>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-600 text-xs text-center">
                    {i18n.language === 'en' ? 'Perfect for: Analysis, optimizations, technical support' : 'Perfecto para: Análisis, optimizaciones, soporte técnico'}
                  </p>
                </div>
              </motion.div>

              {/* Modelo 3: Retainer Mensual */}
              <motion.div variants={scaleIn} className="bg-white rounded-2xl p-8 text-center border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 group hover:shadow-2xl hover:shadow-blue-500/20 relative overflow-hidden aos-init aos-animate">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: '#6B21A8' }}>
                    <Trophy className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {i18n.language === 'en' ? 'Monthly Retainer' : 'Retainer Mensual'}
                  </h4>
                  <p className="text-gray-500 text-sm">
                    {i18n.language === 'en' ? 'Continuous Partnership' : 'Colaboración Continua'}
                  </p>
                </div>

                <ul className="space-y-3 mb-6 text-left">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Dedicated hours each month' : 'Horas dedicadas cada mes'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Priority support and response' : 'Soporte y respuesta prioritaria'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm leading-relaxed">{i18n.language === 'en' ? 'Predictable monthly cost' : 'Costo mensual predecible'}</span>
                  </li>
                </ul>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-600 text-xs text-center">
                    {i18n.language === 'en' ? 'Perfect for: Continuous development, maintenance, evolution' : 'Perfecto para: Desarrollo continuo, mantenimiento, evolución'}
                  </p>
                </div>
              </motion.div>
            </motion.div>

          </div>
        </motion.section>
        
        {/* Contact Section */}
      </div>

      {/* Contact Information Section */}
      <motion.section
        id="contact"
        initial="hidden"
        whileInView="visible"
        viewport={viewportSettings}
        variants={staggerContainer}
        className="py-20"
        style={{
          background: 'white'
        }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <motion.div variants={fadeInUp} className="text-left mb-12">
            <h2 className="text-sm uppercase font-bold text-blue-900 tracking-wide mb-2">
              {i18n.t('contact.sectionTitle')}
            </h2>
            <p className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
              {i18n.t('contact.title')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Information Card */}
            <motion.div variants={fadeInUp} className="">
              <h3 className="text-2xl font-bold text-blue-900 mb-8">
                {i18n.t('contact.call')}
              </h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="rounded-lg p-3 flex-shrink-0" style={{ background: '#0F2A7D' }}>
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-900 mb-1">
                      {i18n.t('contact.email')}
                    </h4>
                    <a
                      href={`mailto:${i18n.t('contact.emailAddress')}`}
                      className="text-lg text-gray-700 leading-relaxed mb-4"
                    >
                      {i18n.t('contact.emailAddress')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="rounded-lg p-3 flex-shrink-0" style={{ background: '#0F2A7D' }}>
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-900 mb-1">
                      {i18n.t('contact.phone')}
                    </h4>
                    <a
                      href={`tel:${i18n.t('contact.phoneNumber')}`}
                      className="text-lg text-gray-700 leading-relaxed mb-4"
                    >
                      {i18n.t('contact.phoneNumber')}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Work Together Card */}
            <motion.div variants={fadeInUp} className="">
              <h3 className="text-2xl font-bold text-blue-900 mb-6">
                {i18n.t('contact.ctaTitle')}
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                {i18n.t('contact.ctaDescription')}
              </p>
              <motion.button
                onClick={() => setIsContactModalOpen(true)}
                className="w-full px-8 py-4 text-white font-semibold rounded-full transition-all duration-200 flex items-center justify-center space-x-2"
                style={{
                  background: '#0F2A7D',
                  boxShadow: '0 4px 14px 0 rgba(15, 42, 125, 0.15)'
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#0A1F5C'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
              >
                <span>{i18n.t('contact.ctaButton')}</span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Footer Section */}
      <footer
        className="py-16 border-t border-white/5"
        style={{
          background: 'linear-gradient(135deg, #0a1628 0%, #0F2A7D 50%, #1a2b4d 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Top Section - Logo & Tagline + Contact Horizontal */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-12">

            {/* Left - Logo & Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-start space-y-3"
            >
              <img
                src="/images/logo_scoutmind_white.png"
                alt="ScoutMind AI Logo"
                className="h-12 w-auto"
                style={{ objectFit: "contain" }}
              />
              <p className="text-blue-200 text-sm leading-relaxed max-w-md">
                {i18n.t('footer.tagline')}
              </p>
            </motion.div>

            {/* Right - Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col items-start md:items-end space-y-2"
            >
              <a
                href={`tel:${i18n.t('footer.phone')}`}
                className="text-blue-100 hover:text-white text-base font-medium transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {i18n.t('footer.phone')}
              </a>
              <a
                href={`mailto:${i18n.t('footer.email')}`}
                className="text-blue-100 hover:text-white text-base font-medium transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {i18n.t('footer.email')}
              </a>
            </motion.div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 mb-8"></div>

          {/* Bottom Section - Legal Links, Social & Copyright */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col md:flex-row justify-between items-center gap-6"
          >

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center md:justify-start items-center gap-3 text-sm order-2 md:order-1">
              <a
                href="/privacy-policy"
                className="text-blue-300 hover:text-white transition-colors duration-200 whitespace-nowrap"
              >
                {i18n.t('footer.links.privacy')}
              </a>
              <span className="text-blue-400/40">•</span>
              <a
                href="/terms-and-conditions"
                className="text-blue-300 hover:text-white transition-colors duration-200 whitespace-nowrap"
              >
                {i18n.t('footer.links.terms')}
              </a>
              <span className="text-blue-400/40">•</span>
              <a
                href="/cookie-policy"
                className="text-blue-300 hover:text-white transition-colors duration-200 whitespace-nowrap"
              >
                {i18n.t('footer.links.cookies')}
              </a>
            </div>

            {/* Center - Copyright */}
            <div className="text-blue-300 text-sm text-center order-3 md:order-2">
              {i18n.t('footer.copyright')}
            </div>

            {/* Right - Social Links */}
            <div className="flex items-center gap-4 order-1 md:order-3">
              <a
                href="https://www.linkedin.com/in/pedro-sc/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:text-white transition-all duration-200 hover:scale-110 p-2 hover:bg-white/10 rounded-lg"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/scoutmindai/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:text-white transition-all duration-200 hover:scale-110 p-2 hover:bg-white/10 rounded-lg"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
      </footer>



      <ContactFormModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </>
  );
};

export default LandingPage;
